/**
 * useAddressBookSync
 *
 * Backs the local address book up to the server as an encrypted VCF blob (#476)
 * and restores it when the server copy is newer. iOS Safari evicts local storage
 * after ~7 days of inactivity, wiping any curation; this keeps a durable, opaque
 * copy on the server keyed to the account.
 *
 * Strategy (mirrors useSnowballSync's structure):
 * - Runs on mount, on foreground, on a fixed interval, and (debounced) whenever
 *   the local book changes (allIds/byId reference change — the same signal the
 *   local auto-save uses).
 * - Each run GETs the server copy and reconciles:
 *     - server strictly newer + no un-synced local edits -> pull silently
 *       (decrypt, parse VCF, replace the book) — unless the book changed while
 *       the run was in flight, in which case the pull is dropped and the next
 *       run decides against the changed book.
 *     - server strictly newer + un-synced local edits, but the server's copy was
 *       last written by THIS device -> our own push whose response we never saw
 *       (cut off after the server committed it): re-push over it, no prompt.
 *     - server strictly newer + un-synced local edits from another device ->
 *       CONFLICT: ask the user which copy to keep, showing when the two copies
 *       diverged. We do not auto-overwrite, and the answer resolves the
 *       divergence on the spot.
 *     - otherwise, if the local book has un-synced changes, PUT it with the last
 *       known version for optimistic concurrency. A 409 is the same conflict.
 * - "Un-synced changes" means user edits only: snowball enrichment, which every
 *   device's background snowball sync rewrites on its own, is left out of the
 *   comparison and rides along with the next real push.
 * - Bookkeeping (last synced server version, that version's server timestamp, and
 *   a signature of the last pushed/pulled book) lives in StorageService under
 *   its own key so the divergence date and change detection survive reloads.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useIonAlert, useIonRouter } from '@ionic/react';
import { useContactState } from './ContactStateContext';
import { selectAllContacts, isSameBook, type BookContents } from './reducer';
import { contactsToVCards } from './util';
import { useUserContext } from '@/modules/user/useUserContext';
import { ClaimsLogic } from '@/modules/idv';
import { StorageService } from '@/services/storage';
import { useAppToast } from '@/hooks/useAppToast';
import {
  readSyncState,
  writeSyncState,
  bookSignature,
  hasUnsyncedChanges,
  fetchServerBackup,
  decryptBackupToContacts,
  pushBackup,
  type ServerBackup,
} from './addressBookBackup';
import type { ContactAction } from './reducer';
import type { ContactList } from './types';
import { ensureDeviceId } from '@/util/deviceId';
import {
  getAddressBookSyncDebounceMs,
  getAddressBookSyncIntervalSeconds,
} from '@/modules/config';
import { useAppStateChange, useAppActiveRef } from '@/hooks/useAppState';
import { decideAddressBookSync } from './addressBookSyncLogic';
import { BackupReminder, PHONE_STEP_PATH } from './backupReminder';

/** Human-readable divergence date for the conflict prompt. */
function formatDivergence(serverVersionAt: string | null): string {
  if (!serverVersionAt) return 'when you last edited on this device';
  const d = new Date(serverVersionAt);
  return Number.isNaN(d.getTime()) ? 'an unknown time' : d.toLocaleString();
}

type ApplyServerCopyResult =
  | { status: 'applied'; contacts: ContactList }
  // The blob can't be decrypted here (e.g. a different phone).
  | { status: 'undecryptable' }
  // The local book changed after the pull was decided.
  | { status: 'superseded' };

/**
 * A background pull's check that the local book is still the one the pull was
 * decided against: the read it was decided on (`basis`) and a getter for the
 * live book.
 */
interface PullGuard {
  basis: BookContents;
  current: () => BookContents;
}

/**
 * Replace the local book with the server's copy and record it as this device's
 * synced state. When the copy can't be decrypted, or (with a `guard`) the local
 * book has changed since the pull was decided, the local book and the
 * bookkeeping are left untouched, so the next run reconciles against the book as
 * it is now.
 */
async function applyServerCopy(
  phone: string,
  server: ServerBackup,
  dispatch: React.Dispatch<ContactAction>,
  guard?: PullGuard
): Promise<ApplyServerCopyResult> {
  let pulled: ContactList;
  try {
    pulled = await decryptBackupToContacts(phone, server);
  } catch {
    return { status: 'undecryptable' };
  }
  if (guard) {
    // An edit committed while the pull was in flight — typically an import,
    // whose file picker backgrounds the app so the resume sync starts from the
    // book as it was before the import — means the pull no longer reflects
    // what the user has. Drop it rather than wipe the edit.
    if (!isSameBook(guard.current(), guard.basis)) {
      return { status: 'superseded' };
    }
    // The live book is read from the last committed render, so an edit
    // dispatched but not yet rendered is caught by the reducer instead. The
    // pull is still recorded below in that case, so the next run backs the
    // edit up over the server's copy rather than asking which to keep.
    dispatch({
      type: 'REPLACE_CONTACTS_IF_UNCHANGED',
      payload: { contacts: pulled, basis: guard.basis },
    });
  } else {
    dispatch({ type: 'SET_CONTACTS', payload: pulled });
  }
  await writeSyncState({
    serverVersion: server.version,
    serverVersionAt: server.updatedAt ?? null,
    // Sign what THIS device serializes for the pulled contacts, so a
    // parse/serialize round-trip doesn't look like a local edit next tick.
    lastSyncedSignature: bookSignature(contactsToVCards(pulled)),
  });
  return { status: 'applied', contacts: pulled };
}

export function useAddressBookSync(): void {
  const { state, dispatch, isLoading } = useContactState();
  const { loading: authLoading, hasUserId, claims } = useUserContext();
  const [presentAlert] = useIonAlert();
  const [presentToast] = useAppToast();
  const router = useIonRouter();

  // Whether the current session may back the address book up: only a
  // phone-verified session pushes, since the encryption key is derived from the
  // account's phone (#476). Pulling/restoring stays allowed so a wiped device can
  // still recover.
  const phoneVerified = ClaimsLogic.isPhoneVerified(claims);

  // Stable snapshot of the latest context so runSync can be a stable callback.
  const ctxRef = useRef({
    state,
    dispatch,
    isLoading,
    authLoading,
    hasUserId,
    phoneVerified,
  });
  useEffect(() => {
    ctxRef.current = {
      state,
      dispatch,
      isLoading,
      authLoading,
      hasUserId,
      phoneVerified,
    };
  });

  const isRunningRef = useRef(false);
  // Set when a run is asked for while one is in flight, so that a book changed
  // under the run in flight is synced once it finishes rather than at the next
  // interval tick.
  const rerunRequestedRef = useRef(false);
  // Once a divergence is surfaced we pause syncing until the user answers the
  // prompt, so it isn't repeated and we never race the resolution's write.
  const conflictShownRef = useRef(false);
  const isActiveRef = useAppActiveRef();

  // Signature of the last-seen book content, so the "verify to back up" nag fires
  // on real user edits — not the initial load, a reset/restore, or background
  // snowball enrichment. Throttled by BackupReminder, which an import's own
  // toast also draws on.
  const nagContentRef = useRef<string | null>(null);

  // Apply the user's answer to the conflict prompt. Either answer ends the
  // divergence outright — one copy wins and this device's bookkeeping records
  // it — so the prompt has no state to return to on the next tick or the next
  // launch. Syncing resumes once the answer has been applied.
  const resolveConflict = useCallback(
    async (keep: 'server' | 'local') => {
      try {
        const { state: currentState, dispatch: currentDispatch } =
          ctxRef.current;
        const phone = await StorageService.getPhone();
        // Re-read the server rather than trusting the copy from the tick that
        // raised the prompt: the user may have sat on it for a while.
        const server = phone ? await fetchServerBackup() : null;
        if (!phone || !server) {
          presentToast({
            message:
              'Could not reach the server. Check your connection and try again.',
            duration: 4000,
            color: 'danger',
          });
          return;
        }

        if (keep === 'server' && server.exists) {
          const applied = await applyServerCopy(phone, server, currentDispatch);
          if (applied.status !== 'applied') {
            presentToast({
              message: 'Could not decrypt the backup on this device.',
              duration: 4000,
              color: 'danger',
            });
            return;
          }
          const pulled = applied.contacts;
          presentToast({
            message: `Restored ${pulled.length} contact${pulled.length === 1 ? '' : 's'} from the other device's copy.`,
            duration: 4000,
            color: 'success',
          });
          return;
        }

        // Keep this device's book (also the path when the server's copy has
        // since disappeared): overwrite the server at the version it just
        // reported, so the write lands rather than 409-ing straight back.
        const vcf = contactsToVCards(selectAllContacts(currentState));
        const result = await pushBackup(phone, vcf, server.version);
        if (result.status !== 'ok') {
          presentToast({
            message:
              "Could not save this device's address book to the server. We'll try again shortly.",
            duration: 4000,
            color: 'danger',
          });
          return;
        }
        await writeSyncState({
          serverVersion: result.version,
          serverVersionAt: result.updatedAt,
          lastSyncedSignature: bookSignature(vcf),
        });
        presentToast({
          message: "This device's address book is now the backup.",
          duration: 4000,
          color: 'success',
        });
      } finally {
        // Resolved or not, resume syncing: a failed resolution (offline, an
        // undecryptable blob) re-surfaces the prompt on a later tick rather
        // than leaving the sync silently switched off.
        conflictShownRef.current = false;
      }
    },
    [presentToast]
  );

  const showConflict = useCallback(
    (serverVersionAt: string | null) => {
      if (conflictShownRef.current) return;
      conflictShownRef.current = true;
      presentAlert({
        header: 'Address book changed on another device',
        message:
          `Your address book was updated on another device (diverging around ${formatDivergence(serverVersionAt)}). ` +
          `Choose which copy to keep — the other one is replaced.`,
        backdropDismiss: false,
        buttons: [
          {
            text: "Keep this device's",
            handler: () => {
              void resolveConflict('local');
            },
          },
          {
            text: "Use the other device's",
            handler: () => {
              void resolveConflict('server');
            },
          },
        ],
      });
    },
    [presentAlert, resolveConflict]
  );

  const runSync = useCallback(async () => {
    if (conflictShownRef.current) return;
    if (isRunningRef.current) {
      // A run asked for mid-flight (e.g. the debounce after an edit) may need
      // the book as it is now, which the run in flight did not see.
      rerunRequestedRef.current = true;
      return;
    }
    const {
      state: currentState,
      dispatch: currentDispatch,
      isLoading: contactsLoading,
      authLoading: authIsLoading,
      hasUserId: identified,
      phoneVerified: verified,
    } = ctxRef.current;
    // The whole backup — read (restore) and write (backup) alike — requires a
    // truly phone-verified session, not merely an identified one. During phone
    // verification the guest is re-identified before the code is confirmed;
    // syncing then would hand back the backup before the user is actually
    // verified. The server enforces the same on both GET and PUT (#476).
    if (authIsLoading || contactsLoading || !identified || !verified) return;

    isRunningRef.current = true;
    try {
      // The encryption key is derived from the user's own phone; without it we
      // can neither decrypt a pulled copy nor produce one the same device can
      // read.
      const phone = await StorageService.getPhone();
      if (!phone) return;

      // The id the server records against each write — the same one apiCall
      // sends.
      const deviceId = await ensureDeviceId();

      const sync = await readSyncState();
      const contacts = selectAllContacts(currentState);
      const localVcf = contactsToVCards(contacts);
      // Snowball enrichment alone is not an edit (see bookSignature): it is
      // backed up with the next real edit rather than counted as a divergence.
      const hasLocalChanges = hasUnsyncedChanges(sync, localVcf);

      // Fetch the server copy (null = transient error, retry next tick).
      const server = await fetchServerBackup();
      if (!server) return;

      // A server copy this device wrote is never someone else's change, even
      // when its version is ahead of our bookkeeping: that gap is a push of ours
      // that committed but whose response never made it back.
      const serverLastWrittenByThisDevice =
        !!server.lastDeviceId && server.lastDeviceId === deviceId;

      const decision = decideAddressBookSync({
        serverExists: server.exists,
        serverVersion: server.version,
        lastSyncedVersion: sync.serverVersion,
        hasLocalChanges,
        localContactCount: contacts.length,
        serverLastWrittenByThisDevice,
      });

      if (decision === 'noop') return;

      if (decision === 'conflict') {
        showConflict(sync.serverVersionAt);
        return;
      }

      if (decision === 'pull') {
        // The pull was decided against the book as this run first read it.
        // Replace it only if that is still the book: an edit made since (an
        // import, say) is a local change the next run must weigh instead. When
        // nothing is applied — an edit got in first, or the blob won't decrypt
        // here (e.g. wrong phone) — the local book is left untouched and the
        // next run reconciles again.
        await applyServerCopy(phone, server, currentDispatch, {
          basis: currentState,
          current: () => ctxRef.current.state,
        });
        return;
      }

      // decision === 'push'. Push against the version we just read rather than
      // our bookkeeping: the reconciler has already ruled out a server copy we
      // would be clobbering, so the only way they differ is a write of our own
      // we never recorded — and pushing at the stale version would 409 forever.
      const expectedVersion = server.exists
        ? server.version
        : sync.serverVersion;
      const result = await pushBackup(phone, localVcf, expectedVersion);
      if (result.status === 'conflict') {
        // Someone wrote between this tick's GET and PUT. If that someone was us
        // (an in-flight push from this device landing late), there is nothing to
        // resolve — the next tick reconciles against the version it reports.
        if (!result.lastDeviceId || result.lastDeviceId !== deviceId) {
          showConflict(sync.serverVersionAt);
        }
        return;
      }
      if (result.status === 'error') return; // transient — retry next tick

      await writeSyncState({
        serverVersion: result.version,
        serverVersionAt: result.updatedAt,
        lastSyncedSignature: bookSignature(localVcf),
      });
    } finally {
      isRunningRef.current = false;
      // A run asked for while this one was in flight matters only if the book
      // changed under it; any other trigger has just been served by this run.
      const rerun =
        rerunRequestedRef.current &&
        !isSameBook(ctxRef.current.state, currentState);
      rerunRequestedRef.current = false;
      if (rerun) void runSyncRef.current();
    }
  }, [showConflict]);
  const runSyncRef = useRef(runSync);
  useEffect(() => {
    runSyncRef.current = runSync;
  }, [runSync]);

  // Interval + initial run while foregrounded.
  useEffect(() => {
    if (isActiveRef.current) runSync();
    const id = setInterval(() => {
      if (isActiveRef.current) runSync();
    }, getAddressBookSyncIntervalSeconds() * 1000);
    return () => clearInterval(id);
  }, [runSync, isActiveRef]);

  // Re-run on return to foreground.
  useAppStateChange(isActive => {
    if (isActive) runSync();
  });

  // Kick a sync the moment the phone becomes verified — so a pending restore
  // (marked by markAddressBookForRestore during a reset) runs immediately instead
  // of waiting for the next interval tick. Gated on phoneVerified so it never
  // fires during verification-in-progress (when the guest is re-identified but
  // not yet verified).
  useEffect(() => {
    if (!authLoading && hasUserId && phoneVerified) runSync();
  }, [authLoading, hasUserId, phoneVerified, runSync]);

  // Debounced flush when the local book changes (allIds or byId reference change,
  // the same signal the local auto-save watches).
  const { allIds, byId } = state;
  useEffect(() => {
    if (isLoading) return;
    const id = setTimeout(runSync, getAddressBookSyncDebounceMs());
    return () => clearTimeout(id);
  }, [allIds, byId, isLoading, runSync]);

  // When an unverified session makes a real edit to the address book, warn (at
  // most once per minute, counting an import toast that already said so) that
  // the change can't be backed up, with a shortcut to verify. The book's
  // signature leaves out snowball enrichment (see bookSignature), so background
  // snowball sync doesn't count as an edit, and it only moves on genuine content
  // changes — so a reset (clear/restore) and the initial load from storage don't
  // trigger the nag either.
  useEffect(() => {
    if (isLoading) return;
    const contacts = allIds.map(id => byId[id]).filter(Boolean) as Parameters<
      typeof contactsToVCards
    >[0];
    const signature = bookSignature(contactsToVCards(contacts));
    const prev = nagContentRef.current;
    nagContentRef.current = signature;

    // First settle establishes the baseline without nagging; only real content
    // changes past that point count.
    if (prev === null || signature === prev) return;
    if (phoneVerified || contacts.length === 0) return;

    if (!BackupReminder.claim()) return;

    presentToast({
      message:
        "We can't back up your address book changes until you verify your phone.",
      duration: 8000,
      color: 'warning',
      buttons: [
        {
          text: 'Verify',
          handler: () => {
            router.push(PHONE_STEP_PATH, 'forward');
          },
        },
      ],
    });
  }, [allIds, byId, isLoading, phoneVerified, presentToast, router]);
}
